import React, { useState } from 'react';
import { getRoleConfig } from '../../../data/roles';
import BpReadingsCard from '../../BpReadingsCard';
import WeightReadingsCard from '../../WeightReadingsCard';
import PatientNotes from '../../PatientNotes';
import TaskCard from '../shared/TaskCard';
import ReportingClock from '../attainment/ReportingClock';
import GoalScoreboard from '../attainment/GoalScoreboard';
import { ROLE_LABELS, REPORTING_KIND_BY_TEMPLATE, templateCodeOf } from '../shared/constants';
import EmptyState from '../shared/EmptyState';
import { CareGoals } from '../../../logic/careGoals';
import { compareOpenTasks, doneStampOf } from '../../../logic/task';

// Overview — patient info recap lives in the pinned header; this tab carries the
// day-to-day work surface: tasks (Active/Done) first, then the CMS goal scoreboard,
// the device-data graphs, notes, and the CMS reporting clock.
export default function OverviewTab({
  patient, userRole, currentUser,
  tasks, allTasksForPatient, bundledTasks, tasksError = false,
  carePeriod, carePlan = null, cmsSubmissions, measureStates, trackCode = null, subtrackId = null,
  onComplete, onReopen, onCloseTask = null, onOrderLab, onAlignCms, onRunCmsEligibility, onRunCmsReporting, onOpenCarePlan,
  onSnooze = null, onResume = null, onNotThisPatient = null,
  onOpenTaskCalendar, onOpenTaskHistory, onCreateTask = null,
}) {
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [taskTab, setTaskTab] = useState('active'); // 'active' | 'done'

  // Chart target lines come from the CARE PLAN — the only thing a human authors. Reading
  // care_goal here showed a target the clinician had already changed, and drew a weight line
  // from a column that holds a PERCENTAGE on 5 live rows (logic/careGoals.ts).
  const planTargets = carePlan?.targets;
  const targetSystolic = CareGoals.systolicTarget(planTargets);
  const targetWeightKg = CareGoals.weightTargetKg(planTargets);

  // The reporting buttons must carry the rpt_* task they satisfy: WF37 gates auto-completion on
  // `!!task_id`, so a null one files the report and leaves the task open on the board.
  const cmsDescriptor = { task_id: null, care_period_id: carePeriod?.care_period_id };
  // Carry the open task due first: the quarter being filed (a retired weight task maps here too).
  const dueMs = (t) => { const ms = Date.parse(t.due_at); return Number.isFinite(ms) ? ms : Infinity; };
  const openReportingTask = (kind) => (allTasksForPatient || tasks || [])
    .filter((t) => REPORTING_KIND_BY_TEMPLATE[templateCodeOf(t)] === kind
      && t.status !== 'completed' && t.status !== 'cancelled')
    .sort((a, b) => dueMs(a) - dueMs(b))[0] || null;

  const isAdmin = userRole === 'care_coordinator';
  // Checklist order: urgent-first → context_json.sequence → priority → due (logic/task.ts).
  const activeTasks = (isAdmin ? (allTasksForPatient || []) : (tasks || [])).slice().sort(compareOpenTasks);
  const otherRoleTasks = isAdmin
    ? []
    : (allTasksForPatient || []).filter(t => !(tasks || []).some(rt => rt.task_id === t.task_id)).sort(compareOpenTasks);
  const otherRoleGroups = {};
  otherRoleTasks.forEach(t => {
    const role = t.owner_role || 'unassigned';
    otherRoleGroups[role] = (otherRoleGroups[role] || 0) + 1;
  });
  const otherCount = otherRoleTasks.length;
  const roleLabel = ROLE_LABELS[userRole] || userRole;
  const sectionLabel = isAdmin ? 'All tasks' : `${roleLabel} tasks`;

  const DONE_STATUSES = new Set(['completed', 'cancelled', 'missed']);
  const roleTaskIds = getRoleConfig(userRole)?.taskRoles || [userRole];
  const doneTasks = (bundledTasks || [])
    .filter(t => (isAdmin || roleTaskIds.includes(t.owner_role)) && DONE_STATUSES.has(t.status))
    .slice()
    .sort((a, b) => {
      const aTs = new Date(doneStampOf(a) || 0).getTime();
      const bTs = new Date(doneStampOf(b) || 0).getTime();
      return bTs - aTs;
    })
    .slice(0, 15);
  const doneCount = doneTasks.length;
  const activeCount = isAdmin
    ? activeTasks.length
    : (showAllTasks ? (tasks.length + otherCount) : tasks.length);

  const tabBtnStyle = (selected) => ({
    background: selected ? 'var(--wi-background-tertiary)' : 'transparent',
    color: selected ? 'var(--wi-text-primary)' : 'var(--wi-text-secondary)',
    border: 'none',
    borderRadius: 20,
    padding: '3px 10px',
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    fontFamily: 'var(--wi-font-family)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  });
  const tabCountStyle = (selected) => ({
    fontSize: 10,
    fontWeight: 700,
    padding: '0 6px',
    borderRadius: 10,
    background: selected ? 'var(--wi-background-primary)' : 'var(--wi-background-secondary)',
    color: 'var(--wi-text-secondary)',
    letterSpacing: 0,
  });

  return (
    <>
      {/* ── Tasks section (role-aware, Active / Done) ────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 8px' }}>
        <span className="eyebrow text-secondary">{sectionLabel}</span>
        {onCreateTask && (
          <button
            onClick={onCreateTask}
            style={{
              marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontSize: 12, fontWeight: 600, color: 'var(--wi-action-primary)', fontFamily: 'var(--wi-font-family)',
            }}
          >
            + New task
          </button>
        )}
        {onCreateTask && <span style={{ fontSize: 12, color: 'var(--wi-text-placeholder)' }}>·</span>}
        <button
          onClick={onOpenTaskCalendar}
          style={{
            marginLeft: onCreateTask ? 0 : 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontSize: 12, fontWeight: 500, color: 'var(--wi-action-primary)', fontFamily: 'var(--wi-font-family)',
          }}
        >
          Calendar
        </button>
        <span style={{ fontSize: 12, color: 'var(--wi-text-placeholder)' }}>·</span>
        <button
          onClick={onOpenTaskHistory}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontSize: 12, fontWeight: 500, color: 'var(--wi-text-secondary)', fontFamily: 'var(--wi-font-family)',
          }}
        >
          List
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 4, margin: '0 0 10px' }}>
        <button type="button" onClick={() => setTaskTab('active')} aria-pressed={taskTab === 'active'} style={tabBtnStyle(taskTab === 'active')}>
          Active
          {activeCount > 0 && <span style={tabCountStyle(taskTab === 'active')}>{activeCount}</span>}
        </button>
        <button type="button" onClick={() => setTaskTab('done')} aria-pressed={taskTab === 'done'} style={tabBtnStyle(taskTab === 'done')}>
          Done
          {doneCount > 0 && <span style={tabCountStyle(taskTab === 'done')}>{doneCount}</span>}
        </button>
      </div>

      {taskTab === 'active' ? (
        <>
          {activeTasks.length === 0 && (
            <EmptyState compact title={tasksError ? 'Task list unavailable' : (isAdmin ? 'No active tasks' : `No ${roleLabel} tasks`)} />
          )}
          {activeTasks.length > 0 && (
            <div className="table-component">
              {activeTasks.map(t => (
                <TaskCard
                  key={t.task_id}
                  task={t}
                  onComplete={onComplete}
                  onCloseTask={onCloseTask}
                  onSnooze={onSnooze}
                  onResume={onResume}
                  onOrderLab={onOrderLab}
                  onAlignCms={onAlignCms}
                  onRunCmsEligibility={onRunCmsEligibility}
                  onRunCmsReporting={onRunCmsReporting}
                  onOpenCarePlan={onOpenCarePlan}
                  viewerRole={userRole}
                  ehrPatientId={patient.ehr_patient_id}
                  suppressRole={isAdmin ? null : userRole}
                />
              ))}
            </div>
          )}

          {!isAdmin && otherCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAllTasks(v => !v)}
              title={Object.entries(otherRoleGroups).map(([r, c]) => `${ROLE_LABELS[r] || r}: ${c}`).join(' · ')}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                width: '100%', marginTop: tasks.length > 0 ? -1 : 0, padding: '10px 14px',
                background: 'var(--wi-background-secondary)',
                border: '1px dashed var(--wi-divider)',
                borderTopLeftRadius: tasks.length > 0 ? 0 : 8,
                borderTopRightRadius: tasks.length > 0 ? 0 : 8,
                borderBottomLeftRadius: 8, borderBottomRightRadius: 8,
                cursor: 'pointer', fontFamily: 'var(--wi-font-family)',
                fontSize: 13, fontWeight: 500, color: 'var(--wi-action-primary)',
              }}
            >
              <span style={{
                display: 'inline-flex', transition: 'transform 200ms ease',
                transform: showAllTasks ? 'rotate(180deg)' : 'rotate(0)',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </span>
              <span>
                {showAllTasks ? 'Hide' : 'Show'} {otherCount} more active task{otherCount > 1 ? 's' : ''} for other role{otherCount > 1 ? 's' : ''}
              </span>
              <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--wi-text-secondary)' }}>
                ({Object.entries(otherRoleGroups).map(([r, c]) => `${ROLE_LABELS[r] || r} ${c}`).join(', ')})
              </span>
            </button>
          )}

          {!isAdmin && showAllTasks && otherRoleTasks.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <div className="table-component" style={{ opacity: 0.7 }}>
                {otherRoleTasks.map(t => (
                  <TaskCard key={t.task_id} task={t} onOrderLab={onOrderLab} onAlignCms={onAlignCms} onRunCmsEligibility={onRunCmsEligibility} onRunCmsReporting={onRunCmsReporting} onOpenCarePlan={onOpenCarePlan} viewerRole={userRole} ehrPatientId={patient.ehr_patient_id} />
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {doneTasks.length === 0 ? (
            <EmptyState compact title="No tasks completed yet" />
          ) : (
            <div className="table-component">
              {doneTasks.map(t => (
                <TaskCard
                  key={t.task_id}
                  task={t}
                  onReopen={onReopen}
                  onNotThisPatient={userRole === 'care_coordinator' ? onNotThisPatient : null}
                  suppressRole={isAdmin ? null : userRole}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* ── CMS goal scoreboard (both staff roles; renders only past an accepted baseline) ── */}
      {(userRole === 'care_coordinator' || userRole === 'clinician') && (
        <GoalScoreboard
          withingsUserId={patient.userid}
          carePeriod={carePeriod}
          carePlan={carePlan}
          measureStates={measureStates}
          cmsSubmissions={cmsSubmissions}
          trackCode={trackCode}
          subtrackId={subtrackId}
        />
      )}

      {/* ── BP Readings (all roles) ──────────────────────────────── */}
      {patient.lastSystolic != null && (
        <BpReadingsCard
          userid={patient.userid}
          targetSystolic={targetSystolic}
          cmsSubmissions={cmsSubmissions}
        />
      )}

      {/* ── Body Metrics: weight + body composition (all roles) ──── */}
      {patient.lastWeight != null && (
        <WeightReadingsCard userid={patient.userid} targetWeightKg={targetWeightKg} cmsSubmissions={cmsSubmissions} />
      )}

      {/* ── Notes (all roles) ─────────────────────────────────────── */}
      {/* key on userid → fresh local draft/edit state when switching patients */}
      <PatientNotes key={patient.userid} patient={patient} currentUser={currentUser} />

      {/* ── CMS reporting clock (both staff roles) ───────────────── */}
      {(userRole === 'care_coordinator' || userRole === 'clinician') && (
        <ReportingClock
          carePeriod={carePeriod}
          cmsSubmissions={cmsSubmissions}
          tasks={allTasksForPatient || tasks}
          onSubmit={(kind) => onRunCmsReporting(openReportingTask(kind) || cmsDescriptor, kind)}
          onAlign={onAlignCms ? () => onAlignCms() : null}
          onRunEligibility={onRunCmsEligibility ? () => onRunCmsEligibility(cmsDescriptor) : null}
        />
      )}
    </>
  );
}
